import React, { useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Inbox, Copy, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  const load = () => api.get("/enquiries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 20000);

  const convert = async (id) => {
    try {
      const { data } = await api.post(`/enquiries/${id}/convert`);
      toast.success(`Ditukar ke ${data.job_number}`);
      nav(`/jobs/${data.id}`);
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry? This cannot be undone.")) return;
    try { await api.delete(`/enquiries/${id}`); toast.success("Enquiry deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete"); }
  };

  const publicUrl = `${window.location.origin}/enquiry`;
  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); };

  return (
    <div className="space-y-4" data-testid="enquiries-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2"><Inbox className="w-6 h-6" /> Enquiries</h1>
          <p className="text-sm text-muted-foreground">Public customer form</p>
        </div>
        <Button variant="outline" onClick={copyLink} data-testid="copy-enquiry-link"><Copy className="w-4 h-4 mr-1" /> Copy Public Form Link</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((e) => (
          <Card key={e.id} className="p-4" data-testid={`enquiry-${e.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display font-bold">{e.customer_name}</div>
                <div className="text-sm text-muted-foreground">{e.phone} • {e.email}</div>
              </div>
              <Badge variant={e.status === "new" ? "default" : "secondary"}>{e.status}</Badge>
            </div>
            <div className="mt-2 text-sm">
              <div><span className="text-muted-foreground">Product:</span> {e.product}</div>
              {e.deadline && <div><span className="text-muted-foreground">Deadline:</span> {e.deadline}</div>}
              {e.details && <div className="mt-1 text-muted-foreground italic">"{e.details}"</div>}
            </div>
            <div className="mt-3 flex justify-between items-center">
              <span className="text-xs text-muted-foreground">{formatDateTime(e.created_at)}</span>
              <div className="flex items-center gap-2">
                {e.status === "new" && <Button size="sm" onClick={() => convert(e.id)} className="bg-cyan-600 hover:bg-cyan-700" data-testid={`convert-${e.id}`}>Convert to Job</Button>}
                <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => remove(e.id)} data-testid={`delete-enquiry-${e.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-muted-foreground">No enquiries</div>}
      </div>
    </div>
  );
}
